<template>
  <div
    :class="[
      'text-field',
      isParticipantPortalField
        ? 'is-participant-portal-field'
        : 'is-general-field',
      { 'is-account-holder-portal-field': isAccountHolderField },
    ]"
  >
    <div
      v-if="isParticipantPortalField && showHeader"
      class="text-field-header-style"
    >
      <span
        v-if="required && (!isAccountHolderField || showRequiredAsterisk)"
        class="error-text"
        >*</span
      >{{ header }}
    </div>
    <v-text-field
      :id="id"
      ref="textfield"
      v-model="text"
      :append-icon="appendIcon"
      :clearable="clearable"
      :color="$colors.penChecksTeal"
      :data-id="dataId"
      :dense="dense"
      :disabled="disabled"
      :errorMessages="errors"
      :hint="hint"
      :label="
        isParticipantPortalField
          ? ''
          : showLabel
          ? label + (required || hideOptionalText ? '' : ' (optional)')
          : ''
      "
      :maxLength="maxLength"
      :outlined="isParticipantPortalField"
      :persistent-hint="persistentHint"
      :placeholder="placeholder"
      :rules="textRules.concat(printabilityRules)"
      :type="type"
      :validate-on-blur="validateBlur"
      @blur="handleBlur"
      @change="$emit('change', text)"
      @focus="errors = null"
      v-on="$listeners"
      @keyup.enter="$emit('enter', $event)"
      @click:clear="$emit('clear')"
    ></v-text-field>
  </div>
</template>
<script>
import { isPOBox } from "@/shared/utils/address-utils";
import {
  PRINTABLE_CHARS_REGEX,
  PRINTABLE_CHARS_ERROR_MESSAGE,
} from "@/shared/lib/printable-chars";
import { nameCharsRule } from "@/shared/lib/name-chars";

export default {
  props: {
    value: {},
    label: {
      type: String,
      required: true,
    },
    type: {
      type: String,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
    },
    appendIcon: {
      type: String,
    },
    id: {
      type: String,
    },
    required: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    clearable: {
      type: Boolean,
    },
    errorMessages: {
      type: Array,
    },
    dense: {
      type: Boolean,
    },
    noPostOfficeBoxes: {
      type: Boolean,
    },
    printableCharsValidation: {
      type: Boolean,
    },
    nameCharsValidation: {
      type: Boolean,
    },
    hint: {
      type: String,
    },
    persistentHint: {
      type: Boolean,
    },
    hideOptionalText: {
      type: Boolean,
    },
    maxLength: {
      type: Number,
    },
    isVerificationCode: {
      type: Boolean,
      default: false,
    },
    isParticipantPortalField: {
      type: Boolean,
      default: false,
    },
    isAccountHolderField: {
      type: Boolean,
      default: false,
    },
    showRequiredAsterisk: {
      type: Boolean,
      default: false,
    },
    header: {
      type: String,
      default: "",
    },
    errorLabel: {
      type: String,
      default: "",
    },
    showHeader: {
      type: Boolean,
      default: false,
    },
    validateBlur: {
      type: Boolean,
      default: true,
    },
    validate: {
      type: Function,
      required: false,
    },
    dataId: {
      type: String,
      required: false,
      validator(value) {
        return value.length == 32;
      },
    },
  },

  data: function () {
    return {
      text: this.value,
      textRules: [
        () => {
          if (this.required && (!this.text || this.text == ""))
            return this.isAccountHolderField
              ? this.errorLabel
              : "Field cannot be left blank";
          return true;
        },
        () => {
          if (this.isVerificationCode && (!this.text || this.text.length != 6))
            return "Your code should be composed of six numbers";
          return true;
        },
        () => {
          if (!this.text || this.text == "" || !this.noPostOfficeBoxes)
            return true;
          if (isPOBox(this.text))
            return "Post office box is not allowed. Please enter a physical address.";
          return true;
        },
        () => {
          if (this.validate && this.text) return this.validate(this.text);
          return true;
        },
      ],
      printabilityRules: [
        (v) => !this.nameCharsValidation || nameCharsRule(v),
        (v) => {
          if (this.printableCharsValidation) {
            if (PRINTABLE_CHARS_REGEX.test(v)) {
              return PRINTABLE_CHARS_ERROR_MESSAGE;
            }
            return true;
          }
          return true;
        },
      ],
      errors: null,
    };
  },
  methods: {
    handleBlur() {
      if (this.value !== null) {
        this.$emit("input", this.value.trim());
        this.$emit("blur");
      }
    },
  },
  watch: {
    text(newValue) {
      this.$emit("input", newValue);
    },
    value(newValue) {
      if (this.text != newValue) {
        this.text = newValue;
      }
    },
    errorMessages(errors) {
      this.errors = errors;
    },
  },

  mounted: function () {
    this.$emit("mounted");
  },
};
</script>

<style lang="scss">
.text-field.is-participant-portal-field {
  .text-field-header-style {
    margin-left: 10px;
    margin-bottom: 8px;
    font-family: "Open Sans", sans-serif;
    color: $neutral70;
    font-size: 12px;
    font-weight: 600;
  }

  .v-input--is-focused {
    .v-input__slot {
      border-width: 1px !important;
      border-color: $primary !important;
      box-shadow: 0px 0px 0px 3px $primary10 !important;
    }
  }

  div.v-input:not(.error--text, .v-input--is-focused) {
    fieldset {
      border-color: $neutral10 !important;
    }
  }

  div.v-text-field {
    border-radius: 6px !important;
  }

  div.v-text-field__slot {
    margin-top: auto;
    margin-bottom: auto;
    height: 20px;
    font-family: "Open Sans", sans-serif;
    input {
      color: $neutral70 !important;
      font-size: 14px;
      height: 20px !important;
    }

    input::placeholder {
      font-family: "Open Sans", sans-serif !important;
      color: $neutral30 !important;
      font-weight: 400 !important;
      font-size: 14px;
    }
  }

  .v-input__slot {
    min-height: 40px !important;
    height: 20px;
  }

  .v-input--is-focused {
    .v-input__slot {
      color: $primary !important;
      caret-color: $primary !important;
      box-shadow: 0px 0px 0px 3px $primary10 !important;
      fieldset {
        border-width: 1px !important;
      }
    }
  }
}
.text-field.is-account-holder-portal-field {
  .v-input__slot {
    margin-bottom: 0;
  }
  .v-text-field__details {
    min-height: 0px;
    margin-bottom: 0px;
    padding-left: 0px;

    div {
      min-height: 0px;
    }

    .error--text {
      margin-top: 8px;
      margin-bottom: 8px;
    }

    .v-messages__wrapper {
      font-size: 10px;
    }
  }
}
</style>
